import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { BannerSwap } from "../../../utils/@types";
import { formatSwapDateCell } from "../bannerDashboardUtils";
import {
  getSwapEndColumnInfo,
  getSwapStartColumnInfo,
  type SwapScheduleColumnInfo,
  type SwapScheduleInfoKind,
  type SwapScheduleInfoLine,
} from "../swapQueueScheduleInfoUtils";
import "./SwapScheduleDateCell.css";

type Props = {
  swap: BannerSwap;
  queue: BannerSwap[];
  field: "startDate" | "endDate";
};

const KIND_LABEL: Record<SwapScheduleInfoKind, string> = {
  overlap: "Schedule overlap",
  delayed: "Delayed start",
  blocked: "Blocked channel",
  adjusted: "Adjusted schedule",
};

function cardClass(kind: SwapScheduleInfoKind): string {
  return `swap-schedule-info__card swap-schedule-info__card--${kind}`;
}

const ScheduleInfoCard: React.FC<{ line: SwapScheduleInfoLine }> = ({
  line,
}) => (
  <div className={cardClass(line.kind)}>
    <p className="swap-schedule-info__channel">{line.channelId}</p>
    <div className="swap-schedule-info__dates">
      <span>{line.effectiveStart}</span>
      <span className="swap-schedule-info__dates-arrow" aria-hidden="true">
        →
      </span>
      <span>{line.effectiveEnd}</span>
    </div>
    <p className="swap-schedule-info__headline">{line.headline}</p>
    {line.subtext ? (
      <p className="swap-schedule-info__subtext">{line.subtext}</p>
    ) : null}
  </div>
);

const SwapScheduleInfoBubble: React.FC<{
  info: SwapScheduleColumnInfo;
  field: "startDate" | "endDate";
}> = ({ info, field }) => {
  const popoverId = useId();
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    setPosition({
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const show = useCallback(() => {
    updatePosition();
    setOpen(true);
  }, [updatePosition]);

  const hide = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        hide();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, hide]);

  const primaryKind = info.lines[0]?.kind ?? "adjusted";
  const title = KIND_LABEL[primaryKind];
  const subtitle =
    field === "startDate"
      ? "Per-channel start times for this swap"
      : "Per-channel end times for this swap";

  return (
    <span
      ref={rootRef}
      className={`swap-schedule-info${open ? " is-open" : ""}`}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <button
        ref={triggerRef}
        type="button"
        className="swap-schedule-info__trigger"
        aria-label={info.ariaSummary}
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((value) => !value);
          if (!open) {
            updatePosition();
          }
        }}
        onFocus={show}
        onBlur={(e) => {
          if (!rootRef.current?.contains(e.relatedTarget as Node)) {
            hide();
          }
        }}
      >
        <i
          className="pi pi-clock swap-schedule-info__trigger-icon"
          aria-hidden="true"
        />
      </button>

      {open
        ? createPortal(
            <div
              id={popoverId}
              className="swap-schedule-info__popover"
              role="tooltip"
              style={{ top: position.top, left: position.left }}
              onMouseEnter={show}
              onMouseLeave={hide}
            >
              <div className="swap-schedule-info__header">
                <span className="swap-schedule-info__header-badge" aria-hidden="true">
                  <i className="pi pi-clock" />
                </span>
                <div className="swap-schedule-info__header-text">
                  <p className="swap-schedule-info__title">{title}</p>
                  <p className="swap-schedule-info__subtitle">{subtitle}</p>
                </div>
              </div>
              <div className="swap-schedule-info__body">
                {info.lines.map((line) => (
                  <ScheduleInfoCard key={line.channelId} line={line} />
                ))}
              </div>
            </div>,
            document.body
          )
        : null}
    </span>
  );
};

const SwapScheduleDateCell: React.FC<Props> = ({ swap, queue, field }) => {
  const info =
    field === "startDate"
      ? getSwapStartColumnInfo(swap, queue)
      : getSwapEndColumnInfo(swap, queue);

  return (
    <span className="swap-schedule-date-cell">
      <span className="swap-schedule-date-cell__time">
        {formatSwapDateCell(swap[field])}
      </span>
      {info ? <SwapScheduleInfoBubble info={info} field={field} /> : null}
    </span>
  );
};

export default SwapScheduleDateCell;
